/**
 * Hook useKanbanConversationColumn - Carrega conversas de uma coluna do Kanban
 * Implementa cursor-based pagination com infinite scroll e filtros server-side
 * Versão: 1.0.0 - 2026-01-29
 * 
 * Segue padrão do useKanbanColumn.ts (CRM) para consistência arquitetural
 */
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';


// Logger para depuração condicional
const logger = createLogger('KanbanConversationColumn');

// Tipo da conversa retornada pelo RPC
export interface KanbanConversationItem {
  id: string;
  kanban_phase_id: string;
  contact_id: string | null;
  contact_name: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  contact_avatar_url: string | null;
  inbox_id: string;
  inbox_name: string | null;
  status: string;
  unread_count: number;
  last_message_at: string | null;
  last_message_preview: string | null;
  created_at: string;
  assigned_to: string | null;
  assigned_agent_name: string | null;
  assigned_agent_avatar: string | null;
  department_id: string | null;
  /** v1.1.0: Tags do contato inline (elimina N+1 SELECTs) */
  contact_tags: Array<{ id: string; name: string; color: string }> | null;
  /** v1.2.0: Dados do grupo (chat_groups) — preenchidos quando a conversa é de grupo */
  group_id: string | null;
  group_name: string | null;
  group_avatar_url: string | null;
  /** v1.3.0: Canal/provider da inbox — usado para o badge do canal sobre o avatar */
  inbox_channel_type: string | null;
  inbox_provider_slug: string | null;
}

// Interface de filtros para o Kanban de Conversas
export interface KanbanConversationFilters {
  inboxId?: string | null;
  agentId?: string | null;
  departmentId?: string | null;
  readStatus?: 'all' | 'read' | 'unread' | null;
  search?: string | null;
}

// Configurações de paginação
const PAGE_SIZE = 10; // carga inicial reduzida (25 -> 10) para acelerar o primeiro render

/**
 * Hook para carregar conversas de uma coluna específica do Kanban de Conversas
 * Utiliza cursor-based pagination para performance em escala
 * Permissões aplicadas server-side via RPC
 * 
 * @param phaseId - ID da fase/coluna do Kanban
 * @param filters - Filtros opcionais (inbox, agente, departamento, leitura, busca)
 * @param enabled - Se deve buscar os dados (default: true)
 */
export function useKanbanConversationColumn(
  phaseId: string | null,
  filters?: KanbanConversationFilters,
  enabled: boolean = true
) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();


  const query = useInfiniteQuery({
    // Query key inclui filtros para cache correto
    queryKey: ['kanban-conversation-column', phaseId, filters],
    queryFn: async ({ pageParam }) => {
      logger.debug(`Buscando conversas da fase ${phaseId}, cursor: ${pageParam}`);

      // Chama RPC com parâmetros de filtro
      // Chamada RPC com cast para unknown (função não tipada no types.ts)
      const { data, error } = await (supabase.rpc as any)('get_kanban_conversations_by_phase', {
        p_phase_id: phaseId!,
        p_limit: PAGE_SIZE,
        p_cursor_last_message_at: pageParam || null,
        p_inbox_id: filters?.inboxId || null,
        p_agent_id: filters?.agentId || null,
        p_department_id: filters?.departmentId || null,
        p_read_status: filters?.readStatus === 'all' ? null : filters?.readStatus || null,
        p_search: filters?.search || null,
        p_tenant_hint: currentTenant?.id || null,
      });

      if (error) {
        logger.error('Erro ao buscar coluna do Kanban de Conversas:', error);
        throw error;
      }

      const result = (data || []) as unknown as KanbanConversationItem[];
      logger.debug(`Recebidas ${result.length} conversas da fase ${phaseId}`);
      return result;
    },
    getNextPageParam: (lastPage) => {
      // Se retornou menos que PAGE_SIZE, não há mais páginas
      if (lastPage.length < PAGE_SIZE) return undefined;
      // Cursor é o last_message_at do último item
      return lastPage[lastPage.length - 1]?.last_message_at;
    },
    initialPageParam: null as string | null,
    enabled: !!phaseId && !!currentTenant?.id && enabled,
    staleTime: 30 * 1000, // 30 segundos
    gcTime: 5 * 60 * 1000, // 5 minutos
  });

  // =========================================================================
  // Realtime: removido daqui em v1.4.0 (2026-06-26).
  // A escuta de mudanças em `conversations` foi centralizada em
  // `useKanbanConversationRealtime`, montado UMA ÚNICA VEZ no KanbanBoard.
  // Isso elimina a explosão de 2×N canais e N invalidações por evento,
  // e habilita patch otimista in-cache para o caso comum (nova mensagem
  // em conversa visível) — zero RPC nesse cenário.
  // =========================================================================


  // Função para invalidar cache desta coluna
  const invalidateColumn = () => {
    queryClient.invalidateQueries({ 
      queryKey: ['kanban-conversation-column', phaseId] 
    });
  };

  // Flatten all pages into a single array
  const conversations = query.data?.pages.flat() || [];

  return {
    conversations,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    invalidateColumn,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Hook para invalidar todas as colunas do Kanban de Conversas
 * Útil após operações que afetam múltiplas colunas (ex: mover conversa)
 */
export function useInvalidateKanbanConversationColumns() {
  const queryClient = useQueryClient();

  return () => {
    // Invalida todas as colunas
    queryClient.invalidateQueries({
      queryKey: ['kanban-conversation-column'],
    });
    // Também invalida os stats das fases
    queryClient.invalidateQueries({
      queryKey: ['kanban-conversation-phase-stats'],
    });
  };
}
